import _styled2 from "@emotion/styled-base";
import _styled from "@emotion/styled-base";
import '@risk-first/ui-column-menu';
import React, { memo, useMemo, useCallback } from 'react';
import { TableData, Box, Flex, Table, TableHead, TableHeader, TableRow, TableBody, TableFoot } from '@risk-first/ui-core';
import { Loading } from '@risk-first/ui-loading';
import { themeGet } from '@styled-system/theme-get';
import { withTheme } from 'emotion-theming';
import { rem } from 'polished';
import { variant } from 'styled-system';
import { Checkbox } from '@risk-first/ui-checkbox';

const defaultFormatter = value => value;

const extractHeaderData = (rowNodes, column, defaultColDef) => {
  const headerProps = Object.assign({
    rowNodes
  }, column.headerComponentParams);
  const specificHeaderComponent = column.headerComponentFramework ? column.headerComponentFramework(headerProps) : null;
  const defaultHeaderComponent = (defaultColDef === null || defaultColDef === void 0 ? void 0 : defaultColDef.headerComponentFramework) ? defaultColDef.headerComponentFramework(headerProps) : null;
  const headerName = column.headerName;
  return specificHeaderComponent || defaultHeaderComponent || headerName;
};

const columnData = (column, rowNode) => {
  var _a, _b;

  const field = (_a = column.field) === null || _a === void 0 ? void 0 : _a.split('.');
  const formatter = (_b = column.valueFormatter) !== null && _b !== void 0 ? _b : defaultFormatter;
  const value = formatter(field === null || field === void 0 ? void 0 : field.reduce((previousValue, currentValue) => {
    return previousValue[currentValue];
  }, rowNode.data));
  const cellProps = Object.assign({
    data: rowNode.data
  }, column.cellRendererParams);
  return {
    cellProps,
    value
  };
};

const StyledTableData = /*#__PURE__*/_styled2(TableData, {
  target: "e1iolpr40",
  label: "StyledTableData"
})("color:", themeGet('colors.text'), ";vertical-align:middle;white-space:nowrap;");

const CellWrapper = /*#__PURE__*/_styled2(Box, {
  target: "e1iolpr41",
  label: "CellWrapper"
})({
  name: "70qvj9",
  styles: "display:flex;align-items:center;"
});

const Cell = props => {
  return React.createElement(StyledTableData, null, React.createElement(CellWrapper, null, props.column.checkboxSelection ? React.createElement(Flex, {
    mr: rem(9)
  }, React.createElement(Checkbox, {
    key: props.rowNode.data.rowMetadata.checkboxKey,
    checked: props.rowNode.data.rowMetadata.isSelected,
    id: props.rowNode.id,
    onChange: props.onRowSelectedProxy(props.rowNodeIndex),
    disabled: props.rowNode.data.rowMetadata.isCheckboxDisabled
  })) : null, props.column.cellRendererFramework ? props.column.cellRendererFramework(props.cellProps) : props.value));
};

const FooterCell = props => {
  var _a;

  if ((_a = props.column.children) === null || _a === void 0 ? void 0 : _a.length) {
    return React.createElement(React.Fragment, null, props.column.children.map(col => React.createElement(TableData, {
      key: `footer-${props.column.colId || props.column.field}-${col.colId || col.field}`
    }, summaryData(col, props.rowNodes))));
  } else {
    return React.createElement(TableData, {
      key: `footer-${props.column.colId || props.column.field}`
    }, summaryData(props.column, props.rowNodes));
  }
};

const summaryData = (col, rowNodes) => {
  if (col.summary) {
    return typeof col.summary === 'function' ? col.summary(rowNodes) : col.summary;
  } else {
    return null;
  }
};

const UI_FRIENDLY_MIN_HEIGHT_FOR_EMPTY_STATE = '100px';

const Wrapper = /*#__PURE__*/_styled(Box, {
  target: "e1iolpr40",
  label: "Wrapper"
})("position:relative;height:inherit;width:inherit;font-family:", themeGet('tableDataFont'), ";font-size:", rem(14), ";overflow:", props => props.isLoading ? 'hidden' : 'auto', ";min-height:", props => props.isLoading || !props.hasRows ? UI_FRIENDLY_MIN_HEIGHT_FOR_EMPTY_STATE : 'inherit', ";");

const StyledTable = /*#__PURE__*/_styled(Table, {
  target: "e1iolpr41",
  label: "StyledTable"
})("position:relative;width:100%;", variant({
  scale: 'table'
}));

const StyledTableHead = /*#__PURE__*/_styled(TableHead, {
  target: "e1iolpr42",
  label: "StyledTableHead"
})({
  name: "epvm6",
  styles: "white-space:nowrap;"
});

const StyledTableHeader = /*#__PURE__*/_styled(TableHeader, {
  target: "e1iolpr43",
  label: "StyledTableHeader"
})("vertical-align:middle;position:sticky;top:0px;border-bottom:1px solid ", themeGet('colors.paleLilac'), ";z-index:1;");

const StyledTableRow = /*#__PURE__*/_styled(TableRow, {
  target: "e1iolpr44",
  label: "StyledTableRow"
})({
  name: "1wuverq",
  styles: "&:hover,&:focus{outline:none;}"
});

const StyledTableRowGroup = /*#__PURE__*/_styled(StyledTableRow, {
  target: "e1iolpr45",
  label: "StyledTableRowGroup"
})({
  name: "ujzx5o",
  styles: "border-bottom:none;"
});

const OverlayWrapper = /*#__PURE__*/_styled(Flex, {
  target: "e1iolpr46",
  label: "OverlayWrapper"
})("position:absolute;z-index:10;width:100%;left:0;justify-content:center;align-items:center;background:hsla(0,0%,100%,0.66);top:", props => props.overlayHeight ? props.overlayHeight : 0, "px;height:", props => props.overlayHeight ? `calc(100% - ${props.overlayHeight}px)` : '100%', ";");

const NativeTable = withTheme(memo(props => {
  const {
    columns,
    rowData,
    defaultColDef,
    headerHeight = 50,
    secondaryHeaderHeight = 20,
    rowHeight = 40,
    isLoading,
    getRowNodeId,
    onRowSelected,
    onRowClicked,
    onRowDoubleClicked,
    variant = 'default'
  } = props;
  const hasSecondaryHeader = useMemo(() => columns.some(column => column.children), [columns]);
  const hasFooter = useMemo(() => rowData.length > 0 && (columns.some(column => column.summary) || columns.some(column => {
    var _a;

    return (_a = column.children) === null || _a === void 0 ? void 0 : _a.some(col => col.summary);
  })), [columns, rowData]);
  const headerStyle = useMemo(() => ({
    height: `${headerHeight}px`
  }), [headerHeight]);
  const overlayHeight = useMemo(() => hasSecondaryHeader ? headerHeight + secondaryHeaderHeight : headerHeight, [hasSecondaryHeader, headerHeight, secondaryHeaderHeight]);
  const rowStyle = useMemo(() => ({
    height: `${rowHeight}px`
  }), [rowHeight]);
  const rowDataLength = (rowData === null || rowData === void 0 ? void 0 : rowData.length) || 0;
  const rowNodes = useMemo(() => (rowData === null || rowData === void 0 ? void 0 : rowData.map((rowData, rowIndex) => ({
    id: getRowNodeId(rowData),
    rowIndex,
    lastRow: rowIndex === rowDataLength - 1,
    data: rowData
  }))) || [], [rowData, rowDataLength, getRowNodeId]);
  const onRowSelectedProxy = useCallback(rowIndex => event => {
    const rowNode = rowNodes[rowIndex];

    if (onRowSelected && !rowNode.data.isCheckboxDisabled) {
      onRowSelected({
        id: rowNode.id,
        isSelected: event.target.checked,
        originalEvent: {
          node: rowNode,
          rowIndex,
          event
        }
      });
    }
  }, [onRowSelected, rowNodes]);
  const onRowClickedProxy = useCallback(rowIndex => event => {
    if (onRowClicked) {
      const rowNode = rowNodes[rowIndex];
      onRowClicked({
        id: rowNode.id,
        originalEvent: {
          node: rowNode,
          rowIndex,
          event
        }
      });
    }
  }, [onRowClicked, rowNodes]);
  const onRowDoubleClickedProxy = useCallback(rowIndex => event => {
    if (onRowDoubleClicked) {
      const rowNode = rowNodes[rowIndex];
      onRowDoubleClicked({
        id: rowNode.id,
        originalEvent: {
          node: rowNode,
          rowIndex,
          event
        }
      });
    }
  }, [onRowDoubleClicked, rowNodes]);
  const handleKeyDown = useCallback(event => {
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      event.currentTarget.previousElementSibling.focus();
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      event.currentTarget.nextElementSibling.focus();
    } else if (event.key === ' ') {
      event.preventDefault();
      event.currentTarget.querySelector('[type="checkbox"]').click();
    }
  }, []);
  return React.createElement(Wrapper, {
    isLoading: isLoading,
    hasRows: rowData.length > 0
  }, !(rowData === null || rowData === void 0 ? void 0 : rowData.length) && !isLoading ? React.createElement(OverlayWrapper, {
    overlayHeight: overlayHeight
  }, "No Rows to Show") : null, isLoading ? React.createElement(OverlayWrapper, {
    overlayHeight: overlayHeight
  }, React.createElement(Loading, null)) : null, React.createElement(StyledTable, {
    variant: variant
  }, React.createElement(StyledTableHead, null, hasSecondaryHeader && React.createElement(StyledTableRowGroup, {
    className: "row-group",
    height: secondaryHeaderHeight
  }, columns.map(column => {
    var _a, _b;

    return React.createElement(TableHeader, {
      key: column.colId || column.field,
      className: "group-cell",
      colSpan: (_a = column.children) === null || _a === void 0 ? void 0 : _a.length,
      scope: "col"
    }, ((_b = column.children) === null || _b === void 0 ? void 0 : _b.length) ? column.headerName : null);
  })), React.createElement(StyledTableRow, null, columns.map(column => {
    var _a;

    if ((_a = column.children) === null || _a === void 0 ? void 0 : _a.length) {
      return column.children.map(childColumn => {
        const headerData = extractHeaderData(rowNodes, childColumn, defaultColDef);
        return React.createElement(StyledTableHeader, {
          key: childColumn.colId || childColumn.field,
          style: headerStyle,
          scope: "col"
        }, headerData);
      });
    } else {
      const headerData = extractHeaderData(rowNodes, column, defaultColDef);
      return React.createElement(StyledTableHeader, {
        key: column.colId || column.field,
        style: headerStyle,
        scope: "col"
      }, headerData);
    }
  }))), React.createElement(TableBody, null, rowNodes.map((rowNode, rowNodeIndex) => {
    return React.createElement(StyledTableRow, {
      key: rowNode.id,
      tabIndex: 0,
      onKeyDown: handleKeyDown,
      onClick: onRowClickedProxy(rowNodeIndex),
      onDoubleClick: onRowDoubleClickedProxy(rowNodeIndex),
      style: rowStyle
    }, columns.map(column => {
      var _a, _b;

      if ((_a = column.children) === null || _a === void 0 ? void 0 : _a.length) {
        return (_b = column.children) === null || _b === void 0 ? void 0 : _b.map(childColumn => {
          const {
            cellProps,
            value
          } = columnData(childColumn, rowNode);
          return React.createElement(Cell, {
            key: childColumn.colId || childColumn.field,
            column: childColumn,
            rowNode: rowNode,
            onRowSelectedProxy: onRowSelectedProxy,
            rowNodeIndex: rowNodeIndex,
            cellProps: cellProps,
            value: value
          });
        });
      } else {
        const {
          cellProps,
          value
        } = columnData(column, rowNode);
        return React.createElement(Cell, {
          key: column.colId || column.field,
          column: column,
          rowNode: rowNode,
          onRowSelectedProxy: onRowSelectedProxy,
          rowNodeIndex: rowNodeIndex,
          cellProps: cellProps,
          value: value
        });
      }
    }));
  })), hasFooter && React.createElement(TableFoot, null, React.createElement(StyledTableRow, null, columns.map(column => React.createElement(FooterCell, {
    key: `footer-container-${column.colId}`,
    column: column,
    rowNodes: rowNodes
  }))))));
}));
export { NativeTable };
//# sourceMappingURL=index.js.map
